import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { employeeApi } from '../../../api/employeeApi';
import BulkActionBar from '../../../components/shared/BulkActionBar';

// Hooks
import { useEmployeeData } from './hooks/useEmployeeData';
import { useEmployeeFilters } from './hooks/useEmployeeFilters';
import { useEmployeeActions } from './hooks/useEmployeeActions';

// Components
import { EmployeeToolbar } from './components/EmployeeToolbar';
import { EmployeeFilters } from './components/EmployeeFilters';
import { EmployeeGrid } from './components/EmployeeGrid';
import { EmployeeTable } from './components/EmployeeTable';
import { CategoryModal } from './components/CategoryModal';
import { EmployeeCardEdit } from './components/EmployeeCardEdit';
import KolonnenView from './components/KolonnenView';
import { useKolonnenFilters } from './hooks/useKolonnenFilters';

// Shared Components
import AbsenceDetailModal from '../../../components/tools/employee/AbsenceDetailModal';
import AbsenceCalendarView from '../../../components/tools/employee/AbsenceCalendarView';
import AbsenceTableView from '../../../components/tools/employee/AbsenceTableView';
import CertificateView from '../../../components/tools/employee/CertificateView';
import UrgencyOverviewModal from '../../../components/tools/employee/UrgencyOverviewModal';

// Utils & Constants
import { STATUS_MAP, LICENSE_OPTIONS } from './employeeConstants';
import { getStatus } from './employeeUtils';
import '../../../components/tools/employee/Employee.css';

const NO_LICENSE_ID = 'no_license';

/**
 * EmployeePage Component
 * Main orchestrator for the employee management tool.
 */
export default function EmployeePage() {
    const navigate = useNavigate();
    const location = useLocation();

    // 1. Data Hook
    const {
        employees,
        categories,

        absences,
        assignments,
        documentTypes,
        documents,
        vehicles,
        kolonnen,
        loading,
        loadAll,
        initializedRef,
        lastCountsRef
    } = useEmployeeData();

    // 2. View State
    const [view, setView] = useState('grid');
    const [kolonnenViewMode, setKolonnenViewMode] = useState('grid');
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [selectedKolonnenIds, setSelectedKolonnenIds] = useState([]);
    const [absView, setAbsView] = useState('table');
    const [absenceWorkerId, setAbsenceWorkerId] = useState(null);
    const [initialAbsenceId, setInitialAbsenceId] = useState(null);
    const [isDeepLinkFromLeader, setIsDeepLinkFromLeader] = useState(false);
    const [urgencyModalOpen, setUrgencyModalOpen] = useState(false);
    
    // Refs for deep linking
    const deepLinkOpenedRef = useRef(false);
    
    // Kolonnen Modal State
    const [isKolonnenModalOpen, setIsKolonnenModalOpen] = useState(false);
    const [currentKolonne, setCurrentKolonne] = useState(null);
    const certViewRef = useRef(null);

    // 3. Filter & Sort Hook
    const {
        searchTerm, setSearchTerm,
        sortConfig, handleSort,
        filterStatus, setFilterStatus,
        filterCategory, setFilterCategory,
        filterWorker, setFilterWorker,
        filterLicense, setFilterLicense,
        filtersRowVisible, setFiltersRowVisible,
        processedEmployees
    } = useEmployeeFilters(employees, categories, absences, view);

    // 3.5 Kolonnen Filter Hook
    const {
        searchTerm: kolonnenSearchTerm, setSearchTerm: setKolonnenSearchTerm,
        filterWorker: kolonnenFilterWorker, setFilterWorker: setKolonnenFilterWorker,
        filterVehicle: kolonnenFilterVehicle, setFilterVehicle: setKolonnenFilterVehicle,
        processedKolonnen
    } = useKolonnenFilters(kolonnen, employees, vehicles);

    // 4. Actions Hook
    const {
        editingId,
        editData, setEditData,
        showAddForm,
        showCategories, setShowCategories,
        attemptedSave,
        handleSave,
        handleDelete,
        startEdit,
        startAdd,
        cancelEdit
    } = useEmployeeActions(loadAll);

    const handleBulkDeleteEmployees = async () => {
        if (window.confirm(`Möchtest du diese ${selectedEmployeeIds.length} Mitarbeiter wirklich unwiderruflich löschen?`)) {
            try {
                await Promise.all(selectedEmployeeIds.map(id => employeeApi.deleteEmployee(id)));
                setSelectedEmployeeIds([]);
                loadAll();
            } catch (err) {
                console.error('Error deleting employees:', err);
                alert('Fehler beim Löschen einiger Mitarbeiter');
            }
        }
    };

    // ---- INITIALIZATION & SYNC ----

    useEffect(() => { loadAll(); }, [loadAll]);

    // Handle Deep Linking
    useEffect(() => {
        let stateChanged = false;
        const newState = { ...location.state };

        if (location.state?.targetTab) {
            setView(location.state.targetTab);
            if (location.state.targetTab === 'calendar') setAbsView('table');
            newState.targetTab = null;
            stateChanged = true;
        }
        if (location.state?.openWorkerId) {
            setAbsenceWorkerId(location.state.openWorkerId);
            newState.openWorkerId = null;
            stateChanged = true;
        }
        if (location.state?.openAbsenceId) {
            setInitialAbsenceId(location.state.openAbsenceId);
            newState.openAbsenceId = null;
            stateChanged = true;
        }
        if (location.state?.from === '/tools/leader') {
            setIsDeepLinkFromLeader(true);
        }
        
        if (location.state?.editWorkerId && processedEmployees.length > 0 && !deepLinkOpenedRef.current) {
            const workerToEdit = processedEmployees.find(e => String(e.id) === String(location.state.editWorkerId));
            if (workerToEdit) {
                deepLinkOpenedRef.current = true;
                startEdit(workerToEdit);
                newState.editWorkerId = null;
                stateChanged = true;
            }
        }

        if (stateChanged) {
            navigate(location.pathname, { replace: true, state: newState });
        }
    }, [location.state, processedEmployees, startEdit, navigate, location.pathname]);

    // Handle opening specific kolonne on deep link
    useEffect(() => {
        if (location.state?.openKolonneId && view === 'kolonnen' && processedKolonnen.length > 0) {
            const kolonneToOpen = processedKolonnen.find(k => String(k.id) === String(location.state.openKolonneId));
            if (kolonneToOpen) {
                setCurrentKolonne(kolonneToOpen);
                setIsKolonnenModalOpen(true);
                // Clear state to avoid reopening on refresh
                navigate(location.pathname, { replace: true, state: { ...location.state, openKolonneId: null } });
            }
        }
    }, [location.state?.openKolonneId, view, processedKolonnen, navigate, location.pathname]);

    // Sync Filter Defaults
    useEffect(() => {
        if (employees.length > 0) {
            if (!initializedRef.current.worker) {
                setFilterWorker(employees.map(e => String(e.id)));
                initializedRef.current.worker = true;
            } else if (employees.length > lastCountsRef.current.employees) {
                setFilterWorker(prev => {
                    if (prev.length === lastCountsRef.current.employees) return employees.map(e => String(e.id));
                    return prev;
                });
            }
        }
        if (categories.length > 0) {
            if (!initializedRef.current.category) {
                setFilterCategory(categories.map(c => String(c.id)));
                initializedRef.current.category = true;
            } else if (categories.length > lastCountsRef.current.categories) {
                setFilterCategory(prev => {
                    if (prev.length === lastCountsRef.current.categories) return categories.map(c => String(c.id));
                    return prev;
                });
            }
        }
        if (!initializedRef.current.status) {
            setFilterStatus(Object.keys(STATUS_MAP));
            initializedRef.current.status = true;
        }
        if (!initializedRef.current.license) {
            setFilterLicense([...LICENSE_OPTIONS, NO_LICENSE_ID]);
            initializedRef.current.license = true;
        }
    }, [employees, categories, setFilterWorker, setFilterCategory, setFilterStatus, setFilterLicense, initializedRef, lastCountsRef]);

    // ---- DERIVED STATE ----

    const { expiredCount, warningCount } = useMemo(() => {
        let expired = 0, warning = 0;
        const now = new Date();
        documents.forEach(doc => {
            if (!doc.expiry_date) return;
            const expiry = new Date(doc.expiry_date);
            const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
            const warningDays = doc.warning_days_override ?? doc.warning_days_default ?? 60;
            if (diffDays < 0) expired++;
            else if (diffDays <= warningDays) warning++;
        });
        return { expiredCount: expired, warningCount: warning };
    }, [documents]);

    // ---- RENDER ----

    return (
        <div className="employee-page" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

            <EmployeeToolbar 
                view={view} setView={setView}
                absView={absView} setAbsView={setAbsView}
                kolonnenViewMode={kolonnenViewMode}
                setKolonnenViewMode={setKolonnenViewMode}
                filtersRowVisible={filtersRowVisible} setFiltersRowVisible={setFiltersRowVisible}
                onAddEmployee={startAdd}
                onManageCategories={() => setShowCategories(true)}
                onAddKolonne={() => { setCurrentKolonne(null); setIsKolonnenModalOpen(true); }}
                searchTerm={view === 'kolonnen' ? kolonnenSearchTerm : searchTerm}
                setSearchTerm={view === 'kolonnen' ? setKolonnenSearchTerm : setSearchTerm}
                certSearchTerm={searchTerm} setCertSearchTerm={setSearchTerm}
                urgencyModalOpen={urgencyModalOpen} setUrgencyModalOpen={setUrgencyModalOpen}
                expiredCount={expiredCount} warningCount={warningCount}
                certViewRef={certViewRef}
            />

            <EmployeeFilters 
                visible={filtersRowVisible}
                filterStatus={filterStatus} setFilterStatus={setFilterStatus}
                filterCategory={filterCategory} setFilterCategory={setFilterCategory}
                filterWorker={view === 'kolonnen' ? kolonnenFilterWorker : filterWorker} 
                setFilterWorker={view === 'kolonnen' ? setKolonnenFilterWorker : setFilterWorker}
                filterLicense={filterLicense} setFilterLicense={setFilterLicense}
                filterVehicle={kolonnenFilterVehicle} setFilterVehicle={setKolonnenFilterVehicle}
                categories={categories} employees={employees} vehicles={vehicles}
                view={view}
            />

            <div className="employee-content">
                {loading ? (
                    <div className="employee-empty-state">
                        <div className="employee-empty-state-icon">⏳</div>
                        <h3>Lade Mitarbeiter...</h3>
                    </div>
                ) : view === 'certificates' ? (
                    <CertificateView 
                        ref={certViewRef}
                        employees={employees} 
                        documents={documents} 
                        documentTypes={documentTypes}
                        onUpdate={loadAll}
                        externalSearch={searchTerm}
                        externalFilterStatus={filterStatus}
                        externalFilterTypeId={filterCategory}
                        externalFilterWorkerId={filterWorker}
                    />
                ) : view === 'kolonnen' ? (
                    <KolonnenView 
                        kolonnen={processedKolonnen} 
                        workers={employees} 
                        vehicles={vehicles} 
                        onUpdate={loadAll}
                        isModalOpen={isKolonnenModalOpen}
                        setIsModalOpen={setIsKolonnenModalOpen}
                        currentKolonne={currentKolonne}
                        setCurrentKolonne={setCurrentKolonne}
                        viewMode={kolonnenViewMode}
                        selectedIds={selectedKolonnenIds}
                        setSelectedIds={setSelectedKolonnenIds}
                    />
                ) : processedEmployees.length === 0 ? (
                    <div className="employee-empty-state">
                        <div className="employee-empty-state-icon">👥</div>
                        <h3>Keine Mitarbeiter gefunden</h3>
                    </div>
                ) : view === 'calendar' ? (
                    absView === 'table' ? (
                        <AbsenceTableView 
                            workers={processedEmployees} absences={absences} 
                            getStatus={(wid) => getStatus(wid, absences)} 
                            sortConfig={{ key: 'name', direction: 'asc' }} 
                            onSort={() => {}}
                            onEntryClick={(wid, aid) => { setAbsenceWorkerId(wid); setInitialAbsenceId(aid); }}
                        />
                    ) : (
                        <AbsenceCalendarView 
                            absences={absences} workers={processedEmployees}
                            onEntryClick={(wid, aid) => { setAbsenceWorkerId(wid); setInitialAbsenceId(aid); }}
                        />
                    )
                ) : view === 'grid' ? (
                    <EmployeeGrid 
                        employees={processedEmployees} absences={absences}
                        onStartEdit={startEdit} onDelete={handleDelete} onManageAbsence={setAbsenceWorkerId}
                    />
                ) : (
                    <>
                        <div className="employee-content-header">
                            <BulkActionBar 
                                selectedCount={selectedEmployeeIds.length}
                                onClearSelection={() => setSelectedEmployeeIds([])}
                                hideSelectionText={true}
                                actions={[
                                    { 
                                        label: `${selectedEmployeeIds.length} Löschen`, 
                                        icon: '🗑️', 
                                        variant: 'danger', 
                                        onClick: handleBulkDeleteEmployees 
                                    }
                                ]}
                            />
                        </div>
                        <EmployeeTable 
                            employees={processedEmployees} absences={absences}
                            sortConfig={sortConfig} onSort={handleSort}
                            onEdit={startEdit} onDelete={handleDelete}
                            onManageAbsence={setAbsenceWorkerId}
                            selectedIds={selectedEmployeeIds}
                            setSelectedIds={setSelectedEmployeeIds}
                        />
                    </>
                )}
            </div>

            {/* MODALS */}
            {showCategories && <CategoryModal categories={categories} onClose={() => setShowCategories(false)} onUpdate={loadAll} />}
            
            {((view === 'grid' || view === 'table') && (editingId || showAddForm)) && (
                <div className="employee-modal-overlay" onClick={cancelEdit}>
                    <div className="employee-modal-container" onClick={e => e.stopPropagation()}>
                        <EmployeeCardEdit 
                            data={editData} onChange={setEditData} 
                            onSave={() => handleSave(editData)} onCancel={cancelEdit}
                            onDelete={handleDelete}
                            categories={categories} employees={employees} licenseOptions={LICENSE_OPTIONS}
                            attemptedSave={attemptedSave}
                        />
                    </div>
                </div>
            )}

            <AbsenceDetailModal 
                isOpen={!!absenceWorkerId} workerId={absenceWorkerId} initialAbsenceId={initialAbsenceId}
                onClose={() => { setAbsenceWorkerId(null); setInitialAbsenceId(null); }}
                workers={employees} assignments={assignments} onUpdate={loadAll}
                showBackToLeader={isDeepLinkFromLeader}
            />

            {urgencyModalOpen && (
                <UrgencyOverviewModal 
                    isOpen={urgencyModalOpen} onClose={() => setUrgencyModalOpen(false)}
                    employees={employees} documents={documents} documentTypes={documentTypes}
                    onEditDoc={(doc) => certViewRef.current?.handleEditDoc(doc)}
                    onSelectEmployee={(wid) => { setUrgencyModalOpen(false); certViewRef.current?.setSelectedWorkerId(wid); }}
                />
            )}
        </div>
    );
}
